import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  IconButton,
  Stack,
  Card,
  CardContent,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  Box,
} from "@mui/material";
import ButtonComponent from "../ButtonComponent";
import CustomMarkdownEditor from "../CustomMarkdownEditor";
import { ReactComponent as RedTrashIcon } from "../../../assets/images/icons/edit_whatsapp_template/trash.svg";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DeleteIcon from "@mui/icons-material/Delete";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";

import "react-quill/dist/quill.snow.css"; // Quill styles
import "quill-emoji/dist/quill-emoji.css"; // Emoji styles
import PlaceholderInput from "../PlaceholderInput";
import TemplateBodyInstructions from "../TemplateBodyInstructions";
import ButtonOne from "../../../common/normal-button";
import typography from "../../../cssUtils/typography";

const EditRegularTemplate = ({
  templateType,
  headerType,
  setHeaderType,
  headerText,
  setHeaderText,
  headerFile,
  setHeaderFile,
  body,
  setBody,
  sampleBodyFields,
  setsampleBodyFields,
  sampleHeaderFields,
  setsampleHeaderFields,
  footer,
  setFooter,
  allButtons,
  setAllButtons,
  isButtonsValid,
  setIsButtonsValid,
  headerDuplicatePlaceholderError,
  setHeaderDuplicatePlaceholderError,
  bodyDuplicatePlaceholderError,
  setBodyDuplicatePlaceholderError,
  headerTextError,
  setHeaderTextError,
  location,
  setLocation,
  locationLatitudeError,
  locationLongitudeError,
  setLocationLatitudeError,
  setLocationLongitudeError,
}) => {
  // const [error, setError] = useState(false);
  const [fileError, setFileError] = useState(false);

  const detectPlaceholders = (text) => {
    const matches = text.match(/\{\{\d+\}\}/g) || [];
    const seen = new Set();
    let hasDuplicate = false;

    matches.forEach((match) => {
      if (seen.has(match)) {
        hasDuplicate = true;
      } else {
        seen.add(match);
      }
    });

    // Only keep the first occurrence of each placeholder
    const uniquePlaceholders = matches.filter(
      (match, idx) => matches.indexOf(match) === idx,
    );
    return { hasDuplicate, uniquePlaceholders };
  };

  const handleBodyChange = (event) => {
    const newBody = event;

    if (newBody === "" || newBody.trim().length > 0) {
      if (newBody.length <= 1024) {
        setBody(newBody);

        const samples = detectPlaceholders(newBody);
        setsampleBodyFields(samples);
      }
    }
  };

  const handleHeaderChange = (event) => {
    const newHeader = event.target.value;

    if (newHeader === "" || newHeader.trim().length > 0) {
      if (newHeader.length <= 60) {
        setHeaderText(newHeader);

        const samples = detectPlaceholders(newHeader);
        setsampleHeaderFields(samples);
      }
    }
  };

  const validateHeaderText = (text) => {
    // Emoji ranges (emoticons, symbols, pictographs, etc.)
    const emojiRegex =
      /\p{Emoji_Presentation}|\p{Extended_Pictographic}️|[\u{1F1E6}-\u{1F1FF}]/u;

    // Disallowed formatting characters
    const formattingCharsRegex = /[*~]/;

    // Newline check
    const newlineRegex = /[\r\n]/;

    if (emojiRegex.test(text)) {
      return "Emojis are not permitted in the header.";
    }
    if (newlineRegex.test(text)) {
      return "Header cannot contain line breaks.";
    }
    if (formattingCharsRegex.test(text)) {
      return "Header cannot contain formatting characters (*, ~).";
    }
    return "";
  };

  const handleFooterChange = (event) => {
    const newFooter = event.target.value;

    if (newFooter === "" || newFooter.trim().length > 0) {
      if (newFooter.length <= 60) {
        setFooter(newFooter);
      }
    }
  };

  const handleHeaderFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // File type validation
    let allowedTypes = [];
    if (headerType === "image") {
      allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    } else if (headerType === "video") {
      allowedTypes = ["video/mp4"];
    } else if (headerType === "document") {
      allowedTypes = ["application/pdf"];
    }

    if (allowedTypes.length && !allowedTypes.includes(file.type)) {
      let typeMsg = "";
      if (headerType === "image") typeMsg = "image (.jpg, .png)";
      else if (headerType === "video") typeMsg = "video (.mp4)";
      else if (headerType === "document") typeMsg = "PDF (.pdf)";
      setFileError(`Invalid file type. Please upload a ${typeMsg} file.`);
      event.target.value = "";
      return;
    }

    // File size validation (existing logic)
    const maxSizes = {
      "video/mp4": 16 * 1024 * 1024, // 16MB for videos
      "image/jpeg": 5 * 1024 * 1024, // 5MB for images
      "image/jpg": 5 * 1024 * 1024, // 5MB for images
      "image/png": 5 * 1024 * 1024, // 5MB for images
      "application/pdf": 5 * 1024 * 1024, // 5MB for pdf
    };

    const maxSize = maxSizes[file.type];
    if (maxSize && file.size > maxSize) {
      const sizeLimit = file.type === "video/mp4" ? "16MB" : "5MB";
      const fileType =
        file.type === "video/mp4"
          ? "video"
          : file.type === "application/pdf"
            ? "PDF"
            : "image";
      setFileError(
        `File size exceeds the limit of ${sizeLimit} for ${fileType} files.`,
      );
      event.target.value = "";
      return;
    }

    // Clear any previous file errors and set the file
    setFileError(false);
    setHeaderFile(file);
  };

  const handleDeleteHeaderFile = () => {
    setFileError(false); // Clear file error when file is deleted
    setHeaderFile(null);
  };

  const getHeaderFileInput = () => {
    const fileTypes = {
      image: {
        accept: ".jpg, .png",
        label: "Choose .JPG or .PNG *",
        sizeLimit: "5MB",
      },
      video: {
        accept: ".mp4",
        label: "Choose .MP4 file *",
        sizeLimit: "16MB",
      },
      document: {
        accept: ".pdf",
        label: "Choose .PDF file *",
        sizeLimit: "5MB",
      },
    };
    if (fileTypes[headerType]) {
      const { accept, label, sizeLimit } = fileTypes[headerType];
      return (
        <div>
          <label htmlFor="file-upload">
            <ButtonOne
              variant="contained"
              component="span"
              sx={{ marginTop: 1, marginBottom: 1, textTransform: "uppercase" }}
            >
              {label}
            </ButtonOne>
            {headerFile && (
              <IconButton aria-label="delete" onClick={handleDeleteHeaderFile}>
                <RedTrashIcon />
              </IconButton>
            )}
          </label>
          <Typography variant="body2" sx={{ fontStyle: "", color: "#353535" }}>
            Maximum file size: {sizeLimit}
          </Typography>
          <input
            id="file-upload"
            type="file"
            accept={accept}
            onChange={handleHeaderFileChange}
            style={{ display: "none" }}
          />

          {/* Display file validation error */}
          {fileError && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mt: 1, display: "block" }}
            >
              {fileError}
            </Typography>
          )}

          {headerFile && (
            <div>
              <Typography variant="body2" sx={{ color: "#353535" }}>
                File uploaded: {headerFile.name}
              </Typography>
            </div>
          )}
        </div>
      );
    } else {
      return null;
    }
  };

  const headerOptions = [
    { value: "none", label: "None" },
    { value: "text", label: "Text" },
    { value: "image", label: "Image" },
    { value: "video", label: "Video" },
    { value: "document", label: "Document" },
    { value: "location", label: "Location" },
  ];

  useEffect(() => {
    if (location) {
      // Latitude validation
      if (
        location.latitude !== undefined &&
        location.latitude !== null &&
        location.latitude !== "" &&
        (location.latitude > 90 || location.latitude < -90)
      ) {
        setLocationLatitudeError(true);
      } else {
        setLocationLatitudeError(false);
      }

      // Longitude validation
      if (
        location.longitude !== undefined &&
        location.longitude !== null &&
        location.longitude !== "" &&
        (location.longitude > 180 || location.longitude < -180)
      ) {
        setLocationLongitudeError(true);
      } else {
        setLocationLongitudeError(false);
      }
    } else {
      setLocationLatitudeError(false);
      setLocationLongitudeError(false);
    }
  }, [location]);

  return (
    <Stack spacing={4}>
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ ...typography.h1, marginBottom: 1 }}>
              Message Header
              {headerType === "none" && (
                <span style={{ opacity: 0.4 }}> (Optional)</span>
              )}
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                aria-label="header-type"
                name="header-type"
                value={headerType}
                onChange={(event) => {
                  const selectedHeaderType = event.target.value;

                  switch (selectedHeaderType) {
                    case "none":
                      setHeaderText("");
                      break;
                    case "text":
                      break;
                    case "image":
                    case "video":
                    case "document":
                      setHeaderText("");
                      break;
                    case "location":
                      setHeaderText("");
                      break;
                    default:
                      break;
                  }

                  setHeaderType(selectedHeaderType);
                }}
                row
                sx={{
                  ...typography.bodyLarge,
                  width: { xs: "100%" },
                  gap: { xs: 1 },
                  flexWrap: "wrap",
                }}
              >
                {headerOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    label={option.label}
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        ...typography.bodyLarge,
                      },
                    }}
                    slotProps={{
                      typography: {
                        sx: {
                          ...typography.bodyLarge,
                        },
                      },
                    }}
                    control={
                      <Radio
                        sx={{
                          color: "#030213",
                          "&.Mui-checked": {
                            color: "#030213",
                          },
                        }}
                      />
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>

            {headerType === "text" && (
              <Grid item xs={12}>
                <TextField
                  sx={{ width: "100%" }}
                  label="Header Text"
                  value={headerText}
                  onChange={(event) => {
                    const newHeader = event.target.value;
                    const errorMsg = validateHeaderText(newHeader);

                    setHeaderTextError(errorMsg);

                    setHeaderText(newHeader);

                    const { hasDuplicate, uniquePlaceholders } =
                      detectPlaceholders(newHeader);
                    setHeaderDuplicatePlaceholderError(hasDuplicate);

                    // Only update sample fields if no error
                    if (!hasDuplicate) {
                      const newSamples = uniquePlaceholders.map((_, idx) =>
                        sampleHeaderFields[idx]?.type
                          ? sampleHeaderFields[idx]
                          : { type: "name", value: "name" },
                      );
                      setsampleHeaderFields(newSamples);
                    }
                    // On a transient duplicate, keep existing sample fields as-is
                    // so previously entered values aren't lost once it resolves.
                  }}
                  error={
                    Boolean(headerTextError) || headerDuplicatePlaceholderError
                  }
                  helperText={
                    headerTextError
                      ? headerTextError
                      : headerDuplicatePlaceholderError
                        ? "Duplicate variable placeholders are not allowed."
                        : ""
                  }
                  inputProps={{
                    maxLength: 60,
                    style: { paddingRight: "40px" },
                  }}
                  InputProps={{
                    endAdornment: (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "gray",
                          position: "absolute",
                          right: "10px",
                          top: "12px",
                        }}
                      >
                        {`${headerText.length}/60`}
                      </Typography>
                    ),
                  }}
                  required
                />
                <Typography variant="body2" sx={{ fontStyle: "" }}>
                  Maximum 1 parameter allowed · Emojis are not permitted.
                </Typography>
              </Grid>
            )}

            {getHeaderFileInput()}

            {headerType === "location" && (
              <div>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Location Name"
                      name="locationparameter_name"
                      required
                      value={location.name}
                      placeholder='E.g: "Galaxy Apartments"'
                      onChange={(e) =>
                        setLocation((prev) => {
                          return {
                            ...prev,
                            name: e.target.value,
                          };
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Location Address"
                      name="locationparameter_address"
                      required
                      value={location.address}
                      placeholder='E.g: "12, Galaxy Apartments, Bandra, Mumbai-400001"'
                      onChange={(e) =>
                        setLocation((prev) => {
                          return {
                            ...prev,
                            address: e.target.value,
                          };
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      inputProps={{ step: "0.000001", min: -90, max: 90 }}
                      label="Location Latitude"
                      name="locationparameter_latitude"
                      required
                      error={locationLatitudeError}
                      helperText={
                        locationLatitudeError &&
                        "Latitude must be between -90 and 90"
                      }
                      value={location.latitude}
                      placeholder="E.g: 19.054072"
                      onChange={(e) =>
                        setLocation((prev) => {
                          return {
                            ...prev,
                            latitude: e.target.value,
                          };
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      inputProps={{ step: "0.000001", min: -180, max: 180 }}
                      label="Location Longitude"
                      name="locationparameter_longitude"
                      required
                      error={locationLongitudeError}
                      helperText={
                        locationLongitudeError &&
                        "Longitude must be between -180 and 180"
                      }
                      value={location.longitude}
                      placeholder="E.g: 72.823279"
                      onChange={(e) =>
                        setLocation((prev) => {
                          return {
                            ...prev,
                            longitude: e.target.value,
                          };
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </div>
            )}

            {!headerDuplicatePlaceholderError &&
              sampleHeaderFields.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
                    Header Variable
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      width: "100%",
                      gap: 4,
                    }}
                  >
                    {sampleHeaderFields.map((sample, index) => (
                      <Box
                        key={index}
                        sx={{
                          marginBottom: "16px",
                          display: "flex",
                          flex: { xs: 1, md: 0.5 },
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ display: "inline-block", mr: 1 }}
                        >
                          {"{{" + (index + 1) + "}}:"}
                        </Typography>
                        <PlaceholderInput
                          sample={sample}
                          index={index}
                          sampleBodyFields={sampleHeaderFields}
                          setsampleBodyFields={setsampleHeaderFields}
                        />
                      </Box>
                    ))}
                  </Box>
                </Grid>
              )}
          </Stack>
        </CardContent>
      </Card>

      {/* Body */}
      <Card>
        <CardContent>
          <Stack spacing={2}>
            <Grid item xs={12}>
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  marginTop: 2,
                  marginBottom: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ marginBottom: 1, fontSize: "20px" }}
                >
                  Message body *
                </Typography>

                <div>
                  {/* The modal will open when this link is clicked */}
                  <TemplateBodyInstructions />
                </div>

                {/* Replace the textarea with our custom editor */}
                <CustomMarkdownEditor
                  id="template-body"
                  value={body}
                  onChange={(newValue) => {
                    setBody(newValue);
                    const { hasDuplicate, uniquePlaceholders } =
                      detectPlaceholders(newValue);
                    setBodyDuplicatePlaceholderError(hasDuplicate);

                    // Only update sample fields if no error
                    if (!hasDuplicate) {
                      const newSamples = uniquePlaceholders.map((_, idx) =>
                        sampleBodyFields[idx]?.type
                          ? sampleBodyFields[idx]
                          : { type: "name", value: "name" },
                      );
                      setsampleBodyFields(newSamples);
                    }
                    // On a transient duplicate, keep existing sample fields as-is
                    // so previously entered values aren't lost once it resolves.
                  }}
                  maxLength={1024}
                />

                {bodyDuplicatePlaceholderError && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    Duplicate variable placeholders are not allowed.
                  </Typography>
                )}
              </div>
            </Grid>
            {!bodyDuplicatePlaceholderError && sampleBodyFields.length > 0 && (
              <Grid item xs={11}>
                <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
                  Body Variables
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    width: "100%",
                    gap: 4,
                  }}
                >
                  {sampleBodyFields.map((sample, index) => (
                    <Box
                      key={index}
                      sx={{
                        marginBottom: "16px",
                        display: "flex",
                        flex: { xs: 1, md: 0.5 },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ display: "inline-block", mr: 1 }}
                      >
                        {"{{" + (index + 1) + "}}:"}
                      </Typography>
                      <PlaceholderInput
                        sample={sample}
                        sampleBodyFields={sampleBodyFields}
                        index={index}
                        setsampleBodyFields={setsampleBodyFields}
                      />
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}

            <Divider />

            {/* Footer */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{ ...typography.h1, fontSize: "20px" }}
              >
                Footer <span style={{ opacity: 0.4 }}> (Optional)</span>
              </Typography>
              <TextField
                sx={{ width: "100%", marginTop: 2, marginBottom: 2 }}
                label="You can use this space to add a tagline, a way to unsubscribe, etc."
                value={footer}
                onChange={handleFooterChange}
                inputProps={{
                  maxLength: 60,
                  style: { paddingRight: "40px" },
                }}
                InputProps={{
                  endAdornment: (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "gray",
                        position: "absolute",
                        right: "10px",
                        top: "12px",
                      }}
                    >
                      {`${footer.length}/60`}
                    </Typography>
                  ),
                }}
              />
              <Typography
                variant="body2"
                sx={{ ...typography.bodyLarge, color: "#353535" }}
              >
                Footer cannot contain any parameters.
              </Typography>
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      {/* Buttons */}
      <Card>
        <CardContent>
          <ButtonComponent
            templateType={templateType}
            allButtons={allButtons}
            setAllButtons={setAllButtons}
            isButtonsValid={isButtonsValid}
            setIsButtonsValid={setIsButtonsValid}
            setFooter={setFooter}
            body={body}
          />
        </CardContent>
      </Card>
    </Stack>
  );
};
export default EditRegularTemplate;
